import {
  FC,
  forwardRef,
  useRef,
  useState,
  useImperativeHandle,
  useCallback,
  useEffect,
} from 'react';

import styles from './plainTextEditor.module.less';

type PlainTextEditorProps = {
  limit?: number;
  onChange?: Function;
  initialValue?: string;
  resetValue?: string;
  defaultText?: string;
  ref: React.Ref<any>;
  disabled?: boolean;
  height?: number | string;
  activateDefaultValue?: boolean;
  detectUserInput?: Function;
};

const PlainTextEditor: FC<PlainTextEditorProps> = forwardRef(
  (props: PlainTextEditorProps, ref) => {
    const {
      limit,
      onChange,
      initialValue = '',
      defaultText = '',
      disabled = false,
      activateDefaultValue = true,
      detectUserInput,
    } = props;

    const [editorContent, setEditorContent] = useState<string>(initialValue);
    const [editorContentLength, setEditorContentLength] = useState<number>(0);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (activateDefaultValue) {
        setEditorContent(defaultText);
      }
    }, [activateDefaultValue, defaultText]);

    useEffect(() => {
      // Initialize character count
      const stripped = stripeContent(editorContent);
      setEditorContentLength(stripped.length);
    }, [editorContent]);

    const stripeContent = (content: string) => {
      let strippedContent = content.replace(/(<([^>]+)>)/gi, ''); // Removes all HTML tags
      strippedContent = strippedContent.replace(/\n/gi, ''); // Removes any newline characters
      strippedContent = strippedContent.replace(/&nbsp;/g, ' '); // Removes any non-breaking space characters
      strippedContent = strippedContent.replace(/&#39;/g, "'"); // replaces unformatted commas

      return strippedContent;
    };

    const checkCharacterCount = (
      event: React.KeyboardEvent<HTMLTextAreaElement>
    ) => {
      detectUserInput && detectUserInput();
      if (limit) {
        if (
          editorContentLength >= limit &&
          event.key !== 'Backspace' &&
          event.key !== 'Delete'
        ) {
          event.preventDefault();
        }
      }
    };

    const handleEditorChange = (
      event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
      const content = event.target.value;

      if (content === '') {
        setEditorContent('');
      } else {
        setEditorContent(content);
      }

      onChange?.(content, true);

      // Update content length
      const strippedContent = stripeContent(content);
      setEditorContentLength(
        strippedContent.trim() === '' ? 0 : strippedContent.length
      );
    };

    const clearContent = useCallback(
      (value?: string) => {
        if (value !== undefined) {
          setEditorContent(value);
          const stripped = stripeContent(value);
          setEditorContentLength(stripped.length);
        } else {
          setEditorContent(initialValue);
          const stripped = stripeContent(initialValue);
          setEditorContentLength(stripped.length);
        }
      },
      [initialValue]
    );

    useImperativeHandle(
      ref,
      () => ({
        clearTextEditorContent(value?: string) {
          clearContent(value);
        },
        getEditorRef() {
          return textareaRef;
        },
        changeEditorContent(value: string) {
          setEditorContent(value);
          const stripped = stripeContent(value);
          setEditorContentLength(stripped.length);
        },
        resetUndo() {
          // No-op for compatibility with RichTextEditor interface
        },
        clearSelection() {
          if (textareaRef.current) {
            textareaRef.current.setSelectionRange(0, 0);
            textareaRef.current.blur();
          }
        },
      }),
      [clearContent]
    );

    return (
      <div className={styles.plainTextEditorWrapper}>
        <textarea
          ref={textareaRef}
          className={`${styles.textarea} ${disabled ? styles.disabled : ''}`}
          value={editorContent}
          onChange={handleEditorChange}
          onKeyDown={checkCharacterCount}
          disabled={disabled}
        />
        {limit && (
          <div className={styles.maxCountLabel}>
            {`Max Character Count: ${editorContentLength}/${limit}`}
          </div>
        )}
      </div>
    );
  }
);

PlainTextEditor.displayName = 'PlainTextEditor';
export default PlainTextEditor;
